import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    LayoutDashboard,
    ShoppingCart,
    Package,
    Boxes,
    BarChart3,
    Users,
    LogOut,
    Wallet,
    DollarSign,
    Gift,
    Menu,
    Receipt,
    Layers,
    Store,
    Clipboard
} from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../ui/sheet';

const Sidebar = () => {
    const { user, logout, isAdmin, isAtendente } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        navigate('/acesso');
    };

    const menuItems = [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', adminOnly: false, hideForAtendente: true },
        { icon: ShoppingCart, label: 'PDV', path: '/admin/pdv', adminOnly: false, hideForAtendente: false },
        { icon: Clipboard, label: 'Comandas', path: '/admin/comandas', adminOnly: false, hideForAtendente: false },
        { icon: Wallet, label: 'Caixa', path: '/admin/caixa', adminOnly: false, hideForAtendente: false },
        { icon: DollarSign, label: 'Financeiro', path: '/admin/financeiro', adminOnly: true, hideForAtendente: true },
        { icon: Receipt, label: 'Despesas', path: '/admin/despesas', adminOnly: false, hideForAtendente: true },
        { icon: Gift, label: 'Combos', path: '/admin/combos', adminOnly: true, hideForAtendente: true },
        { icon: Layers, label: 'Produtos Compostos', path: '/admin/produtos-compostos', adminOnly: true, hideForAtendente: true },
        { icon: Package, label: 'Produtos', path: '/admin/produtos', adminOnly: true, hideForAtendente: true },
        { icon: Boxes, label: 'Estoque', path: '/admin/estoque', adminOnly: true, hideForAtendente: true },
        { icon: BarChart3, label: 'Relatórios', path: '/admin/relatorios', adminOnly: true, hideForAtendente: true },
        { icon: Users, label: 'Usuários', path: '/admin/usuarios', adminOnly: true, hideForAtendente: true },
    ];

    const filteredItems = menuItems.filter(item => {
        if (isAtendente() && item.hideForAtendente) return false;
        if (item.adminOnly && !isAdmin()) return false;
        return true;
    });

    const SidebarContent = ({ isMobile = false }) => (
        <>
            {/* Logo */}
            <div className="p-4 bg-gradient-to-r from-black via-gray-900 to-black border-b border-red-500/30">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-600 to-red-500 flex items-center justify-center shadow-lg shadow-red-500/30">
                        <Store className="w-7 h-7 text-white" />
                    </div>
                    <div>
                        <h1 className="font-bold text-lg text-white">Empório FN</h1>
                        <p className="text-xs text-red-400 font-medium">Mercearia & Minimercado</p>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto bg-gray-950">
                {filteredItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={() => isMobile && setMobileOpen(false)}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                                isActive
                                    ? 'bg-red-500/20 text-red-400 font-semibold border border-red-500/30 shadow-lg shadow-red-500/10'
                                    : 'text-gray-400 hover:text-red-400 hover:bg-gray-900/50'
                            }`
                        }
                    >
                        <item.icon className="w-5 h-5" />
                        <span>{item.label}</span>
                    </NavLink>
                ))}
            </nav>

            {/* User section */}
            <div className="p-4 border-t border-gray-800 bg-gray-950">
                <div className="flex items-center gap-3 px-3 py-2 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-red-600 to-red-500 flex items-center justify-center shadow-lg shadow-red-500/20">
                        <span className="text-white font-bold">
                            {user?.name?.charAt(0).toUpperCase()}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                    </div>
                </div>
                <Button
                    variant="ghost"
                    className="w-full justify-start text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl"
                    onClick={handleLogout}
                >
                    <LogOut className="w-5 h-5 mr-3" />
                    Sair
                </Button>
            </div>
        </>
    );

    return (
        <>
            {/* Mobile Header */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-black via-gray-900 to-black px-4 h-16 flex items-center justify-between shadow-md border-b border-red-500/30">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 to-red-500 flex items-center justify-center shadow-lg shadow-red-500/30">
                        <Store className="w-6 h-6 text-white" />
                    </div>
                    <span className="font-bold text-white">Empório FN</span>
                </div>
                
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                    <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-white hover:bg-red-500/10">
                            <Menu className="w-6 h-6" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-72 p-0 bg-gray-950 border-r border-red-500/30">
                        <SheetHeader className="sr-only">
                            <SheetTitle>Menu de Navegação</SheetTitle>
                        </SheetHeader>
                        <div className="flex flex-col h-full">
                            <SidebarContent isMobile={true} />
                        </div>
                    </SheetContent>
                </Sheet>
            </div>

            {/* Desktop Sidebar */}
            <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-64 bg-gray-950 border-r border-red-500/20 flex-col z-50 shadow-xl">
                <SidebarContent />
            </aside>
        </>
    );
};

export default Sidebar;
